import React from 'react'
import Link from 'next/link'
import classNames from 'classnames'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper'
import 'swiper/css'
import 'swiper/css/autoplay'

import stats, { formattedStats } from 'constants/marketing-stats'
import Footer from 'modules/marketplace/layout/components/Footer'
import Logo from 'components/Logo'
import MarketingFooter from '../MarketingFooter'
import ButtonAnchor from 'components/ButtonAnchor'
import styles from './MarketingWholesalerPageContent.module.css'
import MarketingPricingPanel from '../MarketingPricingPanel'

const cx = classNames.bind(styles)
const logos = [
  'paperie',
  'boyle',
  'johnco',
  'colcam',
  'tamboril',
  'mary-grace',
  'wild-republic',
  'alfresco',
  'enjoy',
  'theatre',
  'vera-may'
]

const MarketingWholesalerPageContent = () => {
  const { annualOrderVolumeMillions } = stats
  return (
    <div className={styles.root}>
      <section className={styles.heroSection}>
        <div className={styles.hero}>
          <div className={styles.logoContainer}>
            <Link href="/" passHref>
              <a className={styles.logo}>
                <Logo isReversed />
              </a>
            </Link>
          </div>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>
              Fieldfolio is the best wholesale platform we’ve come across in 30 years of business.”
            </h1>
            <p className={styles.heroSubheadLine}>
              <strong>Mary Warnest, </strong>
              Porter Green
            </p>
            <div className={styles.heroRating}>
              <img src="/marketing/wholesaler-page/icons/star.svg" alt="" />
              <img src="/marketing/wholesaler-page/icons/star.svg" alt="" />
              <img src="/marketing/wholesaler-page/icons/star.svg" alt="" />
              <img src="/marketing/wholesaler-page/icons/star.svg" alt="" />
              <img src="/marketing/wholesaler-page/icons/star.svg" alt="" />
            </div>
            <div className={styles.buttonContainer}>
              <Link href="/signup/wholesaler" passHref>
                <ButtonAnchor kind="primary" className={styles.button} labelStyle={styles.buttonLabel}>
                  Get Started Today
                </ButtonAnchor>
              </Link>
              <p className={styles.creditCardNote}>No credit card required</p>
            </div>
          </div>
        </div>
      </section>

      <section className={cx(styles.infoSection, styles.brownBackground)}>
        <div className={styles.logoCarouselContainer}>
          <div className={styles.logoCarouselContent}>
            <Swiper
              modules={[Autoplay]}
              slidesPerView={2}
              spaceBetween={40}
              loop={true}
              loopedSlides={logos.length}
              freeMode={true}
              autoplay={{
                delay: 1500,
                disableOnInteraction: false
              }}
              breakpoints={{
                640: {
                  slidesPerView: 3,
                  spaceBetween: 32
                },
                768: {
                  slidesPerView: 4,
                  spaceBetween: 32
                },
                1024: {
                  slidesPerView: 6,
                  spaceBetween: 32
                }
              }}>
              {logos.map((logo, index) => (
                <SwiperSlide key={index}>
                  <span className="swiper-slide" key={index}>
                    <img src={`/marketing/wholesaler-page/logos/${logo}.png`} height="128" width="128" alt="" />
                  </span>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
          <p className={styles.logoCarouselText}>Leading wholesalers choose Fieldfolio</p>
        </div>

        <div className={cx(styles.infoContent, styles.fixedWidth)}>
          <div className={styles.infoItem}>
            <h1>{formattedStats.buyers}+</h1>
            <h3>Registered retailers</h3>
            <p className={styles.infoItemText}>
              Australia&apos;s No. 1 wholesale platform, used by thousands of retailers
            </p>
          </div>
          <div className={styles.infoItem}>
            <h1>{formattedStats.monthlyRetailerGrowth}+</h1>
            <h3>New retailers monthly</h3>
            <p className={styles.infoItemText}>
              Over {formattedStats.monthlyRetailerGrowth} retailers join Fieldfolio every month
            </p>
          </div>
          <div className={styles.infoItem}>
            <h1>{formattedStats.retailerAudience}+</h1>
            <h3>Retailer audience</h3>
            <p className={styles.infoItemText}>
              One of the largest retailer mailing lists in Australia and New Zealand
            </p>
          </div>
          <div className={styles.infoItem}>
            <h1>${annualOrderVolumeMillions}+ million</h1>
            <h3>Wholesale orders annually</h3>
            <p className={styles.infoItemText}>
              Over ${annualOrderVolumeMillions} million in orders annually, and growing fast!
            </p>
          </div>
        </div>
      </section>

      <section className={cx(styles.workflowSection)}>
        <div className={cx(styles.workflowInner, styles.fixedWidth)}>
          <h2 className={cx(styles.sectionHeading, styles.workflowHeading)}>How Fieldfolio works</h2>
          <div className={styles.workflowContentContainer}>
            <div className={styles.workflowContent}>
              <div className={styles.workflowImageContainer}>
                <img src="/marketing/wholesaler-page/icons/create-illustration.svg" alt="Create Illustration" />
              </div>
              <h3>Create your catalog</h3>
              <p>
                Easily upload your products to
                <br />
                Fieldfolio in minutes
              </p>
            </div>
            <div className={cx(styles.workflowContent)}>
              <div className={styles.workflowImageContainer}>
                <img src="/marketing/wholesaler-page/icons/connect-illustration.svg" alt="Connect Illustration" />
              </div>
              <h3>
                Connect with thousands
                <br />
                of retailers and sales reps
              </h3>
              <p>
                Fieldfolio promotes you to thousands
                <br />
                of retailers and sales reps across
                <br />
                Australia and New Zealand
              </p>
            </div>
            <div className={cx(styles.workflowContent)}>
              <div className={styles.workflowImageContainer}>
                <img src="/marketing/wholesaler-page/icons/sales-illustration.svg" alt="Sales Illustration" />
              </div>
              <h3>Start making sales!</h3>
              <p>Retailer orders are sent directly to you</p>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.reviewSection}>
        <div className={styles.reviewContainer}>
          <div className={styles.reviewContent}>
            <h1 className={styles.reviewHeadline}>
              Thank you for the incredible platform you&apos;ve built for businesses like ours.
              <br />
              We didn’t expect we’d get this many new customers, and it’s still early days!”
            </h1>
            <div className={styles.reviewerInfo}>
              <strong>Ilya, </strong>
              CODU
            </div>
            <div className={styles.rating}>
              <img src="/marketing/wholesaler-page/icons/star.svg" alt="" />
              <img src="/marketing/wholesaler-page/icons/star.svg" alt="" />
              <img src="/marketing/wholesaler-page/icons/star.svg" alt="" />
              <img src="/marketing/wholesaler-page/icons/star.svg" alt="" />
              <img src="/marketing/wholesaler-page/icons/star.svg" alt="" />
            </div>
          </div>
        </div>
      </section>

      <section className={cx(styles.featureSection, styles.feature1)}>
        <div className={cx(styles.featureContainer)}>
          <div className={styles.featureImageContainer}>
            <img src="/marketing/wholesaler-page/icons/ipad-iphone-desktop-marketplace-desktop.png" alt="Marketplace" />
          </div>
          <div className={styles.featureContent}>
            <h2 className={cx(styles.featureHeading, styles.sectionHeading)}>
              Sell online to
              <br />
              {formattedStats.buyers}+ retailers
            </h2>
            <div className={styles.featureItem}>
              <img src="/marketing/wholesaler-page/icons/clock.svg" alt="" />
              <div>
                <h3>Be found year round</h3>
                <p>Gain exposure to thousands of retailers and turbo-charge your sales without going to trade shows</p>
              </div>
            </div>
            <div className={styles.featureItem}>
              <img src="/marketing/wholesaler-page/icons/lock.svg" alt="" />
              <div>
                <h3>You’re in control</h3>
                <p>
                  Control which retailers have access to your wholesale pricing and manage customer segments with
                  multiple catalogs
                </p>
              </div>
            </div>
            <div className={styles.featureItem}>
              <img src="/marketing/wholesaler-page/icons/advertise.svg" alt="" />
              <div>
                <h3>Advertise your brand</h3>
                <p>Put yourself ahead of the pack with customised promotions and email campaigns</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className={cx(
          styles.featureSection,
          styles.brownBackground,
          styles.promotionFeatureSection,
          styles.mapSection
        )}>
        <div className={styles.mapSectionContentContainer}>
          <h2 className={styles.mapSectionHeading}>Find wholesale agents on Fieldfolio</h2>
          <div className={styles.mapSectionSubHeading}>
            Australia’s and New Zealand’s largest network of wholesale Sales Reps.
            <br />
            Over one thousand Sales Reps already use Fieldfolio, and are looking to sell for new wholesalers everyday.
          </div>
        </div>
        <div className={styles.mapImageContainer}>
          <img src="/marketing/wholesaler-page/icons/wholesaler-map.png" alt="Sales Reps" />
        </div>
      </section>

      <section className={cx(styles.featureSection, styles.feature2, styles.ipadFeatureSection)}>
        <div className={cx(styles.ipadFeatureContainer)}>
          <div className={styles.ipadFeatureImageContainer}>
            <img src="/marketing/wholesaler-page/icons/ipad-marketplace-desktop.png" alt="Marketplace" />
          </div>
          <div className={styles.featureContent}>
            <div className={styles.featureDescription}>
              <img src="/marketing/wholesaler-page/icons/fieldfolio-ipad-logo.png" width="48px" height="48px" alt="" />
              <strong>Fieldfolio</strong>&nbsp;for iPad
            </div>
            <h2 className={cx(styles.featureHeading, styles.sectionHeading)}>
              Give your sales reps
              <br />
              superpowers
            </h2>
            <div className={styles.featureItem}>
              <img src="/marketing/wholesaler-page/icons/heart.svg" alt="" />
              <div>
                <h3>Industry favourite</h3>
                <p>Keep your sales reps and agents happy with the most intuitive sales and ordering iPad app.</p>
              </div>
            </div>
            <div className={styles.featureItem}>
              <img src="/marketing/wholesaler-page/icons/chart.svg" alt="" />
              <div>
                <h3>Trade show friendly</h3>
                <p>Add customers on the fly and scan barcodes to complete orders in seconds.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={cx(styles.featureSection, styles.brownBackground, styles.promotionFeatureSection)}>
        <div className={cx(styles.featureContainer)}>
          <div className={styles.promotionFeatureContent}>
            <div className={styles.promotionFeatureImageContainer}>
              <img src="/marketing/wholesaler-page/icons/promotions-illustration.svg" alt="Marketplace Promotions" />
            </div>
            <div className={cx(styles.featureContent, styles.promotionFeatureContentText)}>
              <h2 className={cx(styles.featureHeading, styles.sectionHeading)}>
                Boost {<span className={styles.hideForMobile}>your</span>} sales with
                <br />
                Fieldfolio promotions
              </h2>
              <div className={cx(styles.featureItem, styles.promotionFeatureItem)}>
                <h3 className={styles.promotionFeatureHeading}>Premium Retailer Email</h3>
                <p>
                  Promote your catalog along with four products or categories to more than{' '}
                  {formattedStats.retailerAudience} retailers across Australia and New Zealand.
                </p>
              </div>
              <div className={cx(styles.featureItem, styles.promotionFeatureItem)}>
                <h3>Homepage Banner</h3>
                <p>
                  Pur your catalog front and centre on the Fieldfolio homepage, viewed by thousands of retailers every
                  week.
                </p>
              </div>
              <div className={cx(styles.featureItem, styles.promotionFeatureItem)}>
                <h3>Department Features</h3>
                <p>
                  Target retailers relevant to your products by taking the top spot on our new department groups on the
                  homepage and our new department pages.
                </p>
              </div>
            </div>
          </div>
          <div className={styles.feedbackSection}>
            <div className={styles.feedbackCardContainer}>
              <h3 className={styles.feedbackTitle}>
                “An excellent investment. New customer orders covered the cost within two days, and we’ve got
                high-quality prospects.”
              </h3>
              <div className={styles.feedbackAuthorContainer}>
                <img src="/marketing/wholesaler-page/icons/ikoninkLogo.png" alt="" />
                <div className={styles.authorName}>
                  <strong>Peter Berman</strong>
                  <br />
                  Ikonink
                </div>
              </div>
            </div>
            <div className={styles.feedbackCardContainer}>
              <h3 className={styles.feedbackTitle}>
                “The response was astounding! Fieldfolio’s reach is incredible - the volume and quality of the orders we
                got far exceeded our expectations.”
              </h3>
              <div className={styles.feedbackAuthorContainer}>
                <img src="/marketing/wholesaler-page/icons/onlyOrdLuxeLogo.png" alt="" />
                <div className={styles.authorName}>
                  <strong>Sandra Rerrie</strong>
                  <br />
                  Only Orb Luxe Products
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={cx(styles.integrationSection)}>
        <div className={cx(styles.integrationInner, styles.fixedWidth)}>
          <div className={styles.integrationMainIconContainer}>
            <img
              src="/marketing/wholesaler-page/icons/integrations/integration-illustration.svg"
              alt="Integration Illustration"
            />
          </div>
          <div className={styles.integrationContent}>
            <h2 className={cx(styles.integrationHeading, styles.sectionHeading)}>World-class integrations</h2>
            <div>
              <div>
                <div className={styles.integrationIconsContainer}>
                  <img src="/marketing/wholesaler-page/icons/integrations/myob.png" height="128" width="128" alt="" />
                  <img src="/marketing/wholesaler-page/icons/integrations/xero.png" height="128" width="128" alt="" />
                </div>
                <div className={styles.integrationItem}>
                  <h3>Accounting and inventory integrations</h3>
                  <p>Eliminate back-office work with our MYOB and Xero integrations.</p>
                </div>
              </div>
              <div>
                <div className={styles.integrationIconsContainer}>
                  <img src="/marketing/wholesaler-page/icons/integrations/zapier.png" height="128" width="128" alt="" />
                  <img src="/marketing/wholesaler-page/icons/integrations/integromat.svg" height="128" alt="" />
                </div>
                <div className={styles.integrationItem}>
                  <h3>Integrate with anything</h3>
                  <p>
                    Integrate with thousands of apps and platforms via Zapier and Integromat, or build your own using
                    the Fieldfolio API.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={cx(styles.pricingSection, styles.brownBackground)}>
        <div className={cx(styles.pricingInner, styles.fixedWidth)}>
          <div className={styles.pricingOverviewCopy}>
            <img
              className={styles.pricingOverviewIllustration}
              src="/marketing/wholesaler-page/seller-landing-page-pricing-overview.png"
              alt="Fieldfolio B2B marketplace"
            />
            <div className={styles.pricingOverviewType}>
              <h2 className={cx(styles.pricingHeading, styles.sectionHeading)}>
                Simple, predictable pricing.
                <br />
                No commission
              </h2>
              <div>
                <div className={styles.pricingItem}>
                  <p>Your subscription stays the same, even as your order volume grows.</p>
                </div>
              </div>
            </div>
          </div>

          <MarketingPricingPanel />
        </div>
      </section>

      <section className={cx(styles.signupSection)}>
        <div className={cx(styles.signupInner)}>
          <h2 className={styles.signupTitle}>Start selling on Fieldfolio</h2>
          <div className={styles.signupButtonWrapper}>
            <Link href="/signup/wholesaler" passHref>
              <ButtonAnchor kind="primary" className={styles.button} labelStyle={styles.buttonLabel}>
                Sell on Fieldfolio
              </ButtonAnchor>
            </Link>
          </div>
        </div>
      </section>

      <section className={cx(styles.footerSection)}>
        <MarketingFooter />
        <Footer />
      </section>
    </div>
  )
}

export default MarketingWholesalerPageContent
